import { ACCEPTED_MIME_TYPES, MAX_FILE_SIZES } from '../config';
import type { AssetKey, AssetType } from '../types';

// ─── MIME helpers ────────────────────────────────────────────

function mimeToAssetType(mime: string): AssetType | null {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return null;
}

/**
 * Map of file extensions to the canonical MIME type we treat them as.
 * Used when the browser reports an ambiguous MIME (e.g. video/mp4 for
 * an audio-only .mp4 file).
 */
const EXTENSION_MIME_OVERRIDE: Record<string, string> = {
  mp3:  'audio/mpeg',
  wav:  'audio/wav',
  ogg:  'audio/ogg',
  m4a:  'audio/m4a',
  aac:  'audio/aac',
  // Audio-only MP4 / M4A containers report as video/mp4 in some browsers.
  // Map to audio/mp4 so they pass audio-slot validation.
  mp4:  'audio/mp4',
};

/**
 * Resolves the effective MIME type for a file.
 * Falls back to extension lookup when the browser-reported type is missing
 * or mismatches the slot (e.g. video/mp4 for an audio-only file).
 */
function effectiveMime(file: File, accepted: string[]): string {
  // Browser type is already acceptable — use it as-is.
  if (file.type && accepted.includes(file.type)) return file.type;

  // Try to find a better match via extension.
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const byExt = EXTENSION_MIME_OVERRIDE[ext];
  if (byExt) return byExt;

  // Fall back to whatever the browser says.
  return file.type;
}

/**
 * Returns accepted MIME types for a given asset key.
 * Falls back to image+video+audio if key is unknown.
 */
export function acceptedMimesForKey(key: AssetKey): string[] {
  if (key in ACCEPTED_MIME_TYPES) {
    return [...ACCEPTED_MIME_TYPES[key as keyof typeof ACCEPTED_MIME_TYPES]];
  }
  // Dynamic image/video slots
  return [...ACCEPTED_MIME_TYPES.image, ...ACCEPTED_MIME_TYPES.video];
}

/**
 * Returns a string suitable for an <input accept="..."> attribute.
 */
export function acceptAttrForKey(key: AssetKey): string {
  return acceptedMimesForKey(key).join(',');
}

// ─── Validation ───────────────────────────────────────────────

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export function validateFile(file: File, key: AssetKey): ValidationResult {
  const accepted = acceptedMimesForKey(key);

  // Resolve the effective MIME — handles audio-only .mp4 files that the
  // browser incorrectly reports as video/mp4.
  const mime = effectiveMime(file, accepted);

  if (!accepted.includes(mime)) {
    return {
      valid: false,
      error: `"${file.name}" is not an accepted file type for this slot. Accepted: ${accepted.join(', ')}`,
    };
  }

  const assetType = mimeToAssetType(mime);
  if (!assetType) {
    return { valid: false, error: `Unknown file type: ${mime}` };
  }

  const maxBytes = MAX_FILE_SIZES[assetType];
  if (file.size > maxBytes) {
    const maxMB = Math.round(maxBytes / 1024 / 1024);
    const fileMB = (file.size / 1024 / 1024).toFixed(1);
    return {
      valid: false,
      error: `"${file.name}" is ${fileMB} MB — exceeds the ${maxMB} MB limit for ${assetType} files.`,
    };
  }

  return { valid: true };
}

// ─── Media metadata extraction ────────────────────────────────

/**
 * Reads duration, width, and height from a video or audio File using
 * a temporary object URL + HTMLMediaElement. Resolves null on failure.
 */
export function extractMediaMetadata(
  file: File
): Promise<{ duration?: number; width?: number; height?: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    // Treat audio-only .mp4/.m4a files as audio even though the browser
    // may report their MIME type as video/mp4.
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const audioExts = new Set(['mp3', 'wav', 'ogg', 'm4a', 'aac']);
    const effectivelyAudio =
      file.type.startsWith('audio/') || audioExts.has(ext);
    const isVideo = !effectivelyAudio && file.type.startsWith('video/');
    const el = document.createElement(isVideo ? 'video' : 'audio') as
      | HTMLVideoElement
      | HTMLAudioElement;

    el.preload = 'metadata';

    el.onloadedmetadata = () => {
      const result = {
        duration: isFinite(el.duration) ? Math.round(el.duration) : undefined,
        ...(isVideo && el instanceof HTMLVideoElement
          ? { width: el.videoWidth, height: el.videoHeight }
          : {}),
      };
      URL.revokeObjectURL(url);
      resolve(result);
    };

    el.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };

    el.src = url;
  });
}

/**
 * Returns a thumbnail data-URL for an image File.
 */
export function extractImageThumbnail(
  file: File,
  maxSize = 120
): Promise<string | null> {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve(null);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/webp', 0.8));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

// ─── Formatting helpers ───────────────────────────────────────

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
